import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import backArrow from "../../assets/images/backArrow.svg";
import { buildSessionPath, getStoredSession } from "../mmtUserList/mmtFlow";
import { getPatientDetails, updatePatient } from "../mmtUserList/mmtApi";
import { USER_LIST } from "../../routes/routerPath";
import { isWebView, postWebViewMessage } from "../../utlis/webViewBridge";
import "./testperson.scss";

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString("en", { month: "short" }),
}));

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 80 }, (_, i) => currentYear - 21 - i);

const EXERCISES = [
  { value: "low", label: "Low" },
  { value: "moderate", label: "Moderate" },
  { value: "good", label: "Good" },
];

const INJURIES = [
  "knee",
  "hip",
  "heart",
  "shoulder",
  "ankle",
  "eye",
  "arm",
  "hand_wrist",
  "stomach_intestine",
  "lungs",
  "other",
];

const MEDICATIONS = [
  "antiseizure",
  "diabetes",
  "pain_muscle",
  "mood_psych",
  "sedatives",
  "bp_heart",
];

const humanize = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const pickField = (fields, name) =>
  Array.isArray(fields)
    ? fields.find((f) => (f.uniqueId ?? f.name) === name)?.value
    : fields?.[name];

const hydrate = (detail) => {
  const fields = detail?.data || detail || [];
  const arr = Array.isArray(fields) ? fields : [];
  const val = (name) => pickField(arr, name) ?? detail?.[name];
  return {
    step1: {
      firstname: val("first_name") || "",
      lastname: val("last_name") || "",
      testPersonId: val("user_id_from_client") || val("test_person_id") || "",
      gender: val("gender") || "",
      birthYear: val("birth_year") || "",
      birthMonth: val("birth_month") || "",
    },
    step2: {
      zipcode: val("zip_code") || "",
      email: val("email") || "",
      phone: val("mobile_phone_number") || "",
      height: val("height") || "",
      weight: val("weight") || "",
      exercises: val("fitness_level") || "",
      injuries: val("injury_or_surgery") || [],
      medications: val("medications") || [],
    },
  };
};

const isValidName = (s) => /^[\p{L}\p{M}\s'-]+$/u.test(s);
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
const isValidPhone = (s) => /^\d{10}$/.test(s);

export default function MmtEditTestPerson({
  readOnly = false,
  seniorUuidProp,
  onClose,
  onSuccess,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const seniorUuid = seniorUuidProp ?? searchParams.get("seniorUuid");
  const [session] = useState(getStoredSession);
  const [step1, setStep1] = useState({
    firstname: "",
    lastname: "",
    testPersonId: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
  });
  const [step2, setStep2] = useState({
    zipcode: "",
    email: "",
    phone: "",
    height: "",
    weight: "",
    exercises: "",
    injuries: [],
    medications: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session || !seniorUuid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getPatientDetails(session, seniorUuid)
      .then((detail) => {
        if (cancelled || !detail) return;
        const next = hydrate(detail);
        setStep1(next.step1);
        setStep2(next.step2);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load test person details.",
          );
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, seniorUuid]);

  const setF1 = (key, value) => setStep1((prev) => ({ ...prev, [key]: value }));
  const setF2 = (key, value) => setStep2((prev) => ({ ...prev, [key]: value }));
  const toggle = (key, value) =>
    setStep2((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const back = () => {
    if (postWebViewMessage("BACK_TO_USER_LIST")) return;
    if (onClose) {
      onClose();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const done = () => {
    sessionStorage.removeItem("userList");
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const submit = async () => {
    if (!isFormValid || !session || !seniorUuid) return;
    setSaving(true);
    setError("");
    try {
      await updatePatient(session, seniorUuid, step1, step2);
      if (!postWebViewMessage("TEST_PERSON_UPDATED")) done();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save changes.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!session && !isWebView()) return null;

  // ── Field-level validation (only fires when the field has content) ──────────
  const firstNameError =
    step1.firstname.trim() !== "" && !isValidName(step1.firstname);
  const lastNameError =
    step1.lastname.trim() !== "" && !isValidName(step1.lastname);
  const emailError =
    step2.email.trim() !== "" && !isValidEmail(step2.email.trim());
  const phoneError =
    step2.phone.trim() !== "" && !isValidPhone(step2.phone.replace(/\D/g, ""));
  const zipcodeError =
    step2.zipcode.trim() !== "" &&
    step2.zipcode.replace(/\D/g, "").length !== 5;

  const isFormValid =
    step1.firstname.trim() !== "" &&
    !firstNameError &&
    step1.lastname.trim() !== "" &&
    !lastNameError &&
    step1.gender !== "" &&
    step1.birthYear !== "" &&
    step1.birthMonth !== "" &&
    !emailError &&
    !phoneError &&
    !zipcodeError;

  const disabled = readOnly || saving;

  return (
    <main className="mmt-test-person-page">
      <header className="mmt-tp-header">
        <button onClick={back} aria-label="Back" className="mmt-tp-back">
          <img src={backArrow} alt="" />
        </button>
        <h1>{readOnly ? "Test Person" : "Edit Test Person"}</h1>
      </header>

      {loading ? (
        <section className="mmt-tp-form">
          <p>Loading...</p>
        </section>
      ) : (
        <section className="mmt-tp-form">
          {error ? <div className="mmt-tp-error">{error}</div> : null}

          <div className="mmt-field">
            <span>First Name {!readOnly && "*"}</span>
            <input
              value={step1.firstname}
              onChange={(e) => setF1("firstname", e.target.value)}
              maxLength={50}
              className={firstNameError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {firstNameError ? (
              <em className="mmt-tp-hint">
                Only letters, spaces, hyphens, and apostrophes
              </em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>Last Name {!readOnly && "*"}</span>
            <input
              value={step1.lastname}
              onChange={(e) => setF1("lastname", e.target.value)}
              maxLength={50}
              className={lastNameError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {lastNameError ? (
              <em className="mmt-tp-hint">
                Only letters, spaces, hyphens, and apostrophes
              </em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>Test Person ID</span>
            <input value={step1.testPersonId} disabled />
          </div>

          <div className="mmt-field">
            <span>Gender {!readOnly && "*"}</span>
            <div className="mmt-tp-radio-group">
              {GENDERS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  className={`mmt-tp-radio-btn${step1.gender === g.value ? " selected" : ""}`}
                  onClick={() => setF1("gender", g.value)}
                  disabled={disabled}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mmt-tp-row">
            <label className="mmt-field">
              <span>Birth Year {!readOnly && "*"}</span>
              <select
                value={step1.birthYear}
                onChange={(e) =>
                  setF1(
                    "birthYear",
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
                disabled={disabled}
              >
                <option value="">Year</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>

            <label className="mmt-field">
              <span>Birth Month {!readOnly && "*"}</span>
              <select
                value={step1.birthMonth}
                onChange={(e) =>
                  setF1(
                    "birthMonth",
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
                disabled={disabled}
              >
                <option value="">Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mmt-tp-section-divider" />

          <div className="mmt-field">
            <span>Zip Code</span>
            <input
              value={step2.zipcode}
              onChange={(e) =>
                setF2("zipcode", e.target.value.replace(/\D/g, "").slice(0, 5))
              }
              inputMode="numeric"
              maxLength={5}
              className={zipcodeError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {zipcodeError ? (
              <em className="mmt-tp-hint">Zip code must be 5 digits</em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>Email</span>
            <input
              type="email"
              inputMode="email"
              value={step2.email}
              onChange={(e) => setF2("email", e.target.value)}
              className={emailError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {emailError ? (
              <em className="mmt-tp-hint">
                Please enter a valid email address.
              </em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>Phone Number</span>
            <input
              type="tel"
              inputMode="numeric"
              value={step2.phone}
              onChange={(e) =>
                setF2("phone", e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              maxLength={10}
              className={phoneError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {phoneError ? (
              <em className="mmt-tp-hint">
                Enter a valid 10-digit phone number
              </em>
            ) : null}
          </div>

          <div className="mmt-tp-row">
            <label className="mmt-field">
              <span>Height (cm)</span>
              <input
                type="number"
                value={step2.height}
                onChange={(e) => setF2("height", e.target.value)}
                disabled={disabled}
              />
            </label>
            <label className="mmt-field">
              <span>Weight (kg)</span>
              <input
                type="number"
                value={step2.weight}
                onChange={(e) => setF2("weight", e.target.value)}
                disabled={disabled}
              />
            </label>
          </div>

          <div className="mmt-field">
            <span>Exercise Frequency</span>
            <div className="mmt-tp-radio-group">
              {EXERCISES.map((e) => (
                <button
                  key={e.value}
                  type="button"
                  className={`mmt-tp-radio-btn${step2.exercises === e.value ? " selected" : ""}`}
                  onClick={() => setF2("exercises", e.value)}
                  disabled={disabled}
                >
                  {e.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mmt-field">
            <span>Injuries / Surgery</span>
            <div className="mmt-tp-chip-group">
              {INJURIES.map((i) => (
                <button
                  key={i}
                  type="button"
                  className={`mmt-tp-chip${step2.injuries.includes(i) ? " selected" : ""}`}
                  onClick={() => toggle("injuries", i)}
                  disabled={disabled}
                >
                  {humanize(i)}
                </button>
              ))}
            </div>
          </div>

          <div className="mmt-field">
            <span>Medications</span>
            <div className="mmt-tp-chip-group">
              {MEDICATIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`mmt-tp-chip${step2.medications.includes(m) ? " selected" : ""}`}
                  onClick={() => toggle("medications", m)}
                  disabled={disabled}
                >
                  {humanize(m)}
                </button>
              ))}
            </div>
          </div>

          {!readOnly ? (
            <div className="mmt-tp-actions">
              <button
                type="button"
                className="mmt-primary-button"
                onClick={submit}
                disabled={!isFormValid || saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </main>
  );
}
